import React, { useState, useEffect } from 'react';
import { getCommunications } from '../services/api';

export default function ClientAlerts() {
  const [comms, setComms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadComms = async () => {
    setLoading(true);
    try {
      const res = await getCommunications();
      setComms(res.data.communications);
    } catch { showToast('Failed to load communication history.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadComms(); }, []);

  const EXCHANGE_STYLE = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  };

  const totalRecipients = comms.reduce((a, c) => a + (c.recipients_count || 0), 0);
  const todayComms = comms.filter(c =>
    new Date(c.sent_at).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4',
          border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          color: toast.type === 'error' ? '#dc2626' : '#15803d',
          padding: '10px 18px', borderRadius: 8, fontSize: 13,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>{toast.msg}</div>
      )}

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Alerts Sent',    value: comms.length,      color: '#1d4ed8' },
          { label: "Today's Alerts",       value: todayComms,        color: '#7c3aed' },
          { label: 'Total Clients Alerted',value: totalRecipients,   color: '#c2410c' },
          { label: 'Channels Used',        value: 'Email + WhatsApp',color: '#15803d', isText: true },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: c.isText ? 14 : 24, fontWeight: 600, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Communication History</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>All bulk alerts sent to clients via Email and WhatsApp</div>
          </div>
          <button onClick={loadComms} style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
        </div>

        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px 100px 80px 80px 120px 1fr 120px 100px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
          {['#', 'Sent By', 'Exchange', 'Segment', 'Channel', 'Message', 'Recipients', 'Sent At'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading...</div>
        ) : comms.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14, color: '#64748b' }}>No communications sent yet.</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
              Go to Segment Control → Alert Clients to send bulk notifications.
            </div>
          </div>
        ) : (
          comms.map((c, i) => {
            const exStyle = EXCHANGE_STYLE[c.exchange] || { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
            return (
              <div key={c.comm_id} style={{
                display: 'grid', gridTemplateColumns: '60px 100px 80px 80px 120px 1fr 120px 100px',
                padding: '13px 20px', alignItems: 'center',
                borderBottom: i < comms.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                background: i % 2 === 0 ? '#fff' : '#fafafa'
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>#{c.comm_id}</div>

                <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>{c.sent_by || 'Admin'}</div>

                <div>
                  {c.exchange ? (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: exStyle.bg, color: exStyle.color, border: `0.5px solid ${exStyle.border}` }}>
                      {c.exchange}
                    </span>
                  ) : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                </div>

                <div style={{ fontSize: 11, color: '#64748b' }}>{c.segment || '—'}</div>

                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe' }}>
                    ✉ Email
                  </span>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0' }}>
                    💬 WA
                  </span>
                </div>

                <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>
                  {c.message_content || '—'}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#eff6ff', color: '#1d4ed8', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {c.recipients_count || 0}
                  </div>
                  <span style={{ fontSize: 11, color: '#64748b' }}>clients</span>
                </div>

                <div style={{ fontSize: 10, color: '#64748b' }}>
                  {new Date(c.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {comms.length > 0 && (
        <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          {comms.length} records
        </div>
      )}
    </div>
  );
}